import { Star, ExternalLink, ChevronDown, Download, UserPlus } from 'lucide-react';

const STAGE_OPTIONS = [
  { value: 'new', label: 'Nouveau', color: 'bg-gray-100 text-gray-700' },
  { value: 'contacted', label: 'Contacte', color: 'bg-blue-100 text-blue-700' },
  { value: 'replied', label: 'Repondu', color: 'bg-amber-100 text-amber-700' },
  { value: 'meeting', label: 'RDV', color: 'bg-teal-100 text-teal-700' },
  { value: 'deal', label: 'Deal', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'won', label: 'Gagne', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'Perdu', color: 'bg-red-100 text-red-700' },
];

export interface Lead {
  id: string;
  company_name: string;
  company_website: string;
  company_domain: string;
  company_country: string;
  company_size: string;
  contact_first_name: string;
  contact_last_name: string;
  job_title: string;
  headline: string;
  department: string;
  email: string;
  phone: string;
  linkedin_url: string;
  score: number;
  is_top_contact: boolean;
  stage: string;
  owner_id: string | null;
  last_contacted_at: string | null;
  notes: string;
  search_id: string | null;
  created_at: string;
}

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
}

interface LeadsTableProps {
  leads: Lead[];
  teamMembers: TeamMember[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onLeadClick: (lead: Lead) => void;
  onStageChange: (leadId: string, stage: string) => void;
  onOwnerChange: (leadId: string, ownerId: string | null) => void;
  onExportCSV: () => void;
  onBulkAssign: (ownerId: string) => void;
}

export default function LeadsTable({
  leads,
  teamMembers,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onLeadClick,
  onStageChange,
  onOwnerChange,
  onExportCSV,
  onBulkAssign,
}: LeadsTableProps) {
  const allSelected = leads.length > 0 && selectedIds.size === leads.length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border-b border-blue-100">
          <span className="text-sm text-blue-700 font-medium">{selectedIds.size} selectionne(s)</span>
          <div className="flex items-center gap-2 ml-auto">
            <select
              onChange={(e) => { if (e.target.value) onBulkAssign(e.target.value); }}
              className="text-xs border border-blue-200 rounded-lg px-2 py-1 bg-white text-blue-700"
              defaultValue=""
            >
              <option value="" disabled>Assigner a...</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
              ))}
            </select>
            <button
              onClick={onExportCSV}
              className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 font-medium px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="w-10 px-3 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="w-10 px-2 py-3"></th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Entreprise</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Poste</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">LI</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Owner</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Stage</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="w-20 px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {leads.map((lead) => {
              const ownerMember = teamMembers.find((m) => m.id === lead.owner_id);
              return (
                <tr
                  key={lead.id}
                  className="hover:bg-gray-50/50 transition-colors cursor-pointer group"
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={() => onToggleSelect(lead.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-2 py-3">
                    {lead.is_top_contact && (
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    )}
                  </td>
                  <td className="px-3 py-3" onClick={() => onLeadClick(lead)}>
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">{lead.company_name || '-'}</div>
                    {lead.company_website && (
                      <div className="text-xs text-gray-400 truncate max-w-[180px]">{lead.company_domain}</div>
                    )}
                  </td>
                  <td className="px-3 py-3" onClick={() => onLeadClick(lead)}>
                    <div className="text-sm text-gray-900">{lead.contact_first_name} {lead.contact_last_name}</div>
                  </td>
                  <td className="px-3 py-3" onClick={() => onLeadClick(lead)}>
                    <div className="text-sm text-gray-600 truncate max-w-[200px]">{lead.job_title || '-'}</div>
                  </td>
                  <td className="px-3 py-3 text-center" onClick={() => onLeadClick(lead)}>
                    {lead.email ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" title={lead.email} />
                    ) : (
                      <span className="inline-block w-2 h-2 rounded-full bg-gray-300" title="Manquant" />
                    )}
                  </td>
                  <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {lead.linkedin_url ? (
                      <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                        <ExternalLink className="w-4 h-4 mx-auto" />
                      </a>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={lead.owner_id || ''}
                      onChange={(e) => onOwnerChange(lead.id, e.target.value || null)}
                      className="text-xs border-0 bg-transparent text-gray-600 focus:ring-0 p-0 cursor-pointer max-w-[100px]"
                    >
                      <option value="">-</option>
                      {teamMembers.map((m) => (
                        <option key={m.id} value={m.id}>{m.first_name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={lead.stage}
                      onChange={(e) => onStageChange(lead.id, e.target.value)}
                      className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer focus:ring-0 ${
                        STAGE_OPTIONS.find((s) => s.value === lead.stage)?.color || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {STAGE_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-400" onClick={() => onLeadClick(lead)}>
                    {lead.last_contacted_at ? new Date(lead.last_contacted_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-3 py-3" onClick={() => onLeadClick(lead)}>
                    <span className="text-xs text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Ouvrir
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {leads.length === 0 && (
        <div className="text-center py-16 px-4">
          <UserPlus className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucun lead pour le moment</p>
          <p className="text-sm text-gray-400 mt-1">Lance une recherche pour generer tes premiers leads</p>
        </div>
      )}
    </div>
  );
}
